import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};
const emptyStr = (v) => (v === '' || v == null ? null : v);

const enrichBudgetCodeRows = async (rows, tenantId) => {
  const properties = await apiFetch(
    `conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`
  ).catch(() => []);
  const nameMap = Object.fromEntries(
    (Array.isArray(properties) ? properties : []).map((p) => [p.businessUnitId, p.propertyName])
  );
  return rows.map((row) => ({
    ...row,
    businessUnitName: nameMap[row.businessUnitId] || '',
  }));
};

const defaultForm = {
  code: '',
  codeName: '',
  codeType: 'BUDGET',
  businessUnitId: '',
  status: 'ACTIVE',
};

const mapBudgetCodeRow = (item) => ({
  id: item.budgetCodeId,
  code: item.code,
  codeName: item.codeName,
  codeType: item.codeType,
  businessUnitId: item.businessUnitId || '',
  businessUnitName: '',
  status: item.status,
  raw: item,
});

const BudgetCodeMaster = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.BUDGET_REVENUE_CODES);
  const tenantId = getTenantId();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const loadBusinessUnits = useCallback(async () => {
    if (!tenantId) return;
    try {
      const data = await apiFetch(`conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`);
      setBusinessUnits(Array.isArray(data) ? data : []);
    } catch {
      setBusinessUnits([]);
    }
  }, [tenantId]);

  const fetchBudgetCodes = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`conductor/budget-codes/tenant/${tenantId}`);
      let mapped = (Array.isArray(data) ? data : []).map(mapBudgetCodeRow);
      mapped = await enrichBudgetCodeRows(mapped, tenantId);
      setRows(mapped);
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load budget codes');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    loadBusinessUnits();
    fetchBudgetCodes();
  }, [loadBusinessUnits, fetchBudgetCodes]);

  const columns = useMemo(() => [
    { field: 'code', headerName: 'Code', width: 140, resizable: true },
    { field: 'codeName', headerName: 'Code Name', width: 220, resizable: true },
    { field: 'codeType', headerName: 'Type', width: 110, resizable: true },
    { field: 'businessUnitName', headerName: 'Business Unit', width: 180, resizable: true },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      resizable: true,
      renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
    },
  ], []);

  const buildPayload = () => ({
    tenantId,
    code: form.code?.trim(),
    codeName: emptyStr(form.codeName),
    codeType: form.codeType || 'BUDGET',
    businessUnitId: emptyStr(form.businessUnitId),
    status: form.status || 'ACTIVE',
  });

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setForm({
      code: selectedRow.code || '',
      codeName: selectedRow.codeName || '',
      codeType: selectedRow.codeType || 'BUDGET',
      businessUnitId: selectedRow.businessUnitId || '',
      status: selectedRow.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    try {
      setSubmitting(true);
      const userId = getUserId();
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/budget-codes', {
          method: 'POST',
          body: JSON.stringify({ ...payload, createdBy: userId, updatedBy: userId }),
        });
        showSuccess('Budget / Revenue Codes saved successfully');
      } else {
        await apiFetch(`conductor/budget-codes/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify({ ...payload, updatedBy: userId }),
        });
        showSuccess('Budget / Revenue Codes updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchBudgetCodes();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/budget-codes/${rowPendingDelete.id}`, { method: 'DELETE' });
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      fetchBudgetCodes();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>Budget / Revenue Codes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit Budget Code' : 'No edit permission'}><span><IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canAdd ? 'Add Budget Code' : 'No add permission'}><span><IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}><AddIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canDelete ? 'Delete Budget Code' : 'No delete permission'}><span><IconButton onClick={() => { setRowPendingDelete(selectedRow); setDeleteOpen(true); }} disabled={!selectedRow || !canDelete} sx={harmonatePlainIconButtonSx}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro rows={rows} columns={columns} loading={loading} rowHeight={40} pagination pageSizeOptions={[10, 25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 100 } } }} rowSelectionModel={selectionModel} onRowSelectionModelChange={setSelectionModel} sx={{ ...harmonateDataGridSx, flex: 1, minHeight: 0, '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' } }} />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Budget / Revenue Code' : 'Edit Budget / Revenue Code'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" required label="Code" value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} />
            <TextField fullWidth size="small" label="Code Name" value={form.codeName} onChange={(e) => setForm((p) => ({ ...p, codeName: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" select label="Code Type" value={form.codeType} onChange={(e) => setForm((p) => ({ ...p, codeType: e.target.value }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="BUDGET">Budget</MenuItem>
              <MenuItem value="REVENUE">Revenue</MenuItem>
            </TextField>
            <TextField fullWidth size="small" select label="Business Unit" value={form.businessUnitId} onChange={(e) => setForm((p) => ({ ...p, businessUnitId: e.target.value }))} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true, MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value=""><em>None</em></MenuItem>
              {businessUnits.map((bu) => (
                <MenuItem key={bu.businessUnitId || bu.propertyId} value={bu.businessUnitId || bu.propertyId}>{bu.propertyName}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" select label="Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>{submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}><Typography sx={{ color: ht.text }}>Are you sure you want to delete &quot;{rowPendingDelete?.codeName || rowPendingDelete?.code || 'selected'}&quot;?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>{deleteLoading ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} title={toastState.title} autoHideDuration={toastState.autoHideDuration} position={toastState.position} />
    </Box>
  );
};

export default BudgetCodeMaster;
